import { isBefore } from 'date-fns';
import { useCallback, useReducer } from 'react';

import { assertIsDateRange, DateRange, DateValue } from '../types';

export type DateRangeControlOptions = {
	onChange: (value: DateRange) => void;
	value: DateRange;
};

type ControlMode = 'idle' | 'selectEnd';

type State = {
	mode: ControlMode;
	first: Date | null;
	last: Date | null;
};

const initialState: State = {
	mode: 'idle',
	first: null,
	last: null,
};

type Action = { type: 'SelectStart'; value: Date } | { type: 'ConsiderEndDate'; value: Date } | { type: 'Reset' };

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'SelectStart':
			return {
				...state,
				mode: 'selectEnd',
				first: action.value,
				last: action.value,
			};

		case 'ConsiderEndDate':
			return {
				...state,
				last: action.value,
			};

		case 'Reset':
			return initialState;

		default:
			return state;
	}
};

const createRange = (first: Date, last: Date): DateRange => {
	if (isBefore(first, last)) {
		return [first, last];
	}

	return [last, first];
};

export const useDateRangeControl = ({ onChange, value }: DateRangeControlOptions) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const selectDate = useCallback(
		(date: DateValue) => {
			if (!date) {
				return;
			}

			if (state.mode === 'idle') {
				// If we're idle, we're picking the first date in the range, so just
				// save that in state an advance to the next mode
				dispatch({ type: 'SelectStart', value: date });
			} else if (state.mode === 'selectEnd') {
				// If we're selecting the second date in the range it could be before or
				// after the first date, so we need to order them into a proper range.
				if (!state.first || !state.last) {
					throw new Error('Invalid range state');
				}

				onChange(createRange(state.first, date));
				dispatch({ type: 'Reset' });
			}
		},
		[onChange, state]
	);

	const hoverDate = useCallback(
		(date: Date) => {
			if (state.mode === 'selectEnd') {
				dispatch({ type: 'ConsiderEndDate', value: date });
			}
		},
		[state]
	);

	// Bring the internal state machine into line with an externally-set range
	// (e.g. someone typed into the header inputs and committed). Without this,
	// the next calendar click would treat the click as a brand-new SelectStart
	// and silently drop the typed boundary.
	const seedRange = useCallback((range: DateRange) => {
		assertIsDateRange(range);
		const [start, end] = range;
		// Handle either boundary symmetrically. createRange sorts ascending
		// when the second click lands, so anchoring on whichever side the user
		// already filled in works in both directions (typed start + clicked
		// end, or typed end + clicked start).
		const anchor = start ?? end;

		if (anchor && !(start && end)) {
			dispatch({ type: 'SelectStart', value: anchor });
			return;
		}

		dispatch({ type: 'Reset' });
	}, []);

	const [selectionStart, selectionEnd] = (() => {
		if (state.mode === 'idle') {
			return value;
		}

		if (!state.first || !state.last) {
			throw new Error('Invalid range state');
		}

		return createRange(state.first, state.last);
	})();

	return {
		selectionEnd,
		selectionStart,
		onHoverDate: hoverDate,
		onSelectDate: selectDate,
		seedRange,
	};
};
